import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import api from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

export default function ChatWidget() {
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [session] = useState(() => Math.random().toString(36).slice(2));
  const endRef = useRef(null);

  const enabled = settings?.chatbot?.enabled;
  const greeting = settings?.chatbot?.greeting || "How can we help style your voyage?";

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  if (!enabled) return null;

  const send = async () => {
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chatbot/message", { message: msg, session_id: session });
      setMessages((m) => [...m, { role: "bot", text: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "bot", text: "Apologies — I'm unavailable right now. Please try again shortly." }]);
    }
    setLoading(false);
  };

  return (
    <>
      <button data-testid="chat-toggle-btn" onClick={() => setOpen((o) => !o)}
              className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-ink text-linen flex items-center justify-center shadow-lg hover:scale-105 transition-transform duration-300">
        {open ? <X size={22} /> : <MessageCircle size={22} strokeWidth={1.5} />}
      </button>

      {open && (
        <div data-testid="chat-window" className="fixed bottom-24 right-6 z-40 w-[92vw] max-w-sm h-[28rem] bg-linen border hairline flex flex-col shadow-2xl">
          <div className="bg-ink text-linen px-5 py-4">
            <p className="font-heading text-xl">Sew Voyage Concierge</p>
            <p className="font-body text-[0.65rem] tracking-wide2 uppercase text-linen/60">Styling · Sizing · Fabric Origins</p>
          </div>
          <div className="flex-1 overflow-y-auto no-scrollbar px-4 py-4 space-y-3">
            <div className="bg-cream px-4 py-3 max-w-[85%] font-body text-sm">{greeting}</div>
            {messages.map((m, i) => (
              <div key={i} className={`px-4 py-3 max-w-[85%] font-body text-sm ${m.role === "user" ? "bg-ink text-linen ml-auto" : "bg-cream"}`} data-testid={`chat-msg-${m.role}`}>{m.text}</div>
            ))}
            {loading && <div className="bg-cream px-4 py-3 max-w-[60%] font-body text-sm text-ink/50">Typing…</div>}
            <div ref={endRef} />
          </div>
          <div className="border-t hairline flex items-center px-3 py-2">
            <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                   placeholder="Ask about fit, fabric, styling…" className="flex-1 bg-transparent outline-none font-body text-sm py-2 px-2" />
            <button onClick={send} className="icon-thin text-ink" data-testid="chat-send-btn"><Send size={18} /></button>
          </div>
        </div>
      )}
    </>
  );
}
